import React, { useEffect, useMemo, useState } from 'react';

import { Box, Checkbox, Grid } from '@mui/material';
import {
  AxisConfig,
  ChartsLegend,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  ChartsYAxisProps,
  LineSeries,
  LineChart,
} from '@mui/x-charts';

import { ChartData } from '../../@types/KingdomResponse';

interface Props {
  dataSet: ChartData[];
  hasMultiple: boolean;
  kd1: string;
  kd2: string;
}

const colors = ['#4CAF50', '#388E3C', '#F44336', '#D32F2F'];

interface ValuesByType {
  landValues: number[];
  nwValues: number[];
  honorValues: number[];
}

interface AxisSeries {
  yAxis: AxisConfig<'linear', any, ChartsYAxisProps>[];
  series: LineSeries[];
}

const createYAxis = (
  id: string,
  label: string,
  values: number[],
  width = 80,
  minFactor = 0.8,
  maxFactor = 1.05
): AxisConfig<'linear', any, ChartsYAxisProps> => {
  const hasValues = values.length > 0;
  return {
    id,
    axis: 'y',
    label,
    scaleType: 'linear',
    min: hasValues ? Math.min(...values) * minFactor : 0,
    max: hasValues ? Math.max(...values) * maxFactor : 100,
    width,
  };
};

const createSeries = (
  keys: string[],
  yAxisId: string,
  colors: string[],
  labelsPrefix: string[],
  showMark = false,
  area = false
): LineSeries[] => {
  return keys.map((key, idx) => ({
    type: 'line',
    dataKey: key,
    label: `${labelsPrefix[idx]} ${yAxisId.replace('Axis', '')}`,
    yAxisId,
    showMark,
    area,
    color: colors[idx],
  }));
};

const collectValues = (dataSet: ChartData[], keys: string[]): number[] => {
  const toNum = (v: unknown) => Number(v);

  const values: number[] = [];

  keys.forEach((key) => {
    values.push(
      ...dataSet
        .map((item) => toNum((item as any)[key]))
        .filter(Number.isFinite)
    );
  });

  return values.length ? values : [0];
};

const NwLandGraph: React.FC<Props> = ({ dataSet, hasMultiple, kd1, kd2 }) => {
  const [labels, setLabels] = useState<string[]>(['Land', 'Networth']);

  const valuesByType = useMemo<ValuesByType>(() => {
    const landKeys = hasMultiple ? ['kd1Land', 'kd2Land'] : ['kd1Land'];
    const nwKeys = hasMultiple ? ['kd1Nw', 'kd2Nw'] : ['kd1Nw'];
    const honorKeys = hasMultiple ? ['kd1Honor', 'kd2Honor'] : ['kd1Honor'];
    return {
      landValues: collectValues(dataSet, landKeys),
      nwValues: collectValues(dataSet, nwKeys),
      honorValues: collectValues(dataSet, honorKeys),
    };
  }, [dataSet, hasMultiple]);

  const yAxisData = useMemo<AxisSeries>(() => {
    const newAxis: AxisConfig<'linear', any, ChartsYAxisProps>[] = [];
    let seriesList: LineSeries[] = [];

    // Land axis and series, always present
    newAxis.push(
      createYAxis('landAxis', 'Land', valuesByType.landValues, 80, 0.95, 1.1)
    );
    seriesList = seriesList.concat(
      createSeries(
        hasMultiple ? ['kd1Land', 'kd2Land'] : ['kd1Land'],
        'landAxis',
        hasMultiple ? [colors[0], colors[2]] : [colors[0]],
        hasMultiple ? [kd1, kd2] : [kd1],
        true,
        false
      )
    );

    // Networth axis and series
    if (labels.includes('Networth')) {
      newAxis.push(
        createYAxis('nwAxis', 'Networth', valuesByType.nwValues, 100)
      );
      seriesList = seriesList.concat(
        createSeries(
          hasMultiple ? ['kd1Nw', 'kd2Nw'] : ['kd1Nw'],
          'nwAxis',
          hasMultiple ? [colors[1], colors[3]] : [colors[1]],
          hasMultiple ? [kd1, kd2] : [kd1],
          false,
          false
        )
      );
    }

    // Honor axis and series
    if (labels.includes('Honor')) {
      newAxis.push(createYAxis('honorAxis', 'Honor', valuesByType.honorValues));
      seriesList = seriesList.concat(
        createSeries(
          hasMultiple ? ['kd1Honor', 'kd2Honor'] : ['kd1Honor'],
          'honorAxis',
          hasMultiple ? [colors[1], colors[3]] : [colors[1]],
          hasMultiple ? [kd1, kd2] : [kd1],
          false,
          false
        )
      );
    }

    return { yAxis: newAxis, series: seriesList };
  }, [valuesByType, kd1, kd2, hasMultiple, labels]);

  const handleLabelClick = (label: string) => {
    if (labels.includes(label)) {
      setLabels(labels.filter((x) => x !== label));
    } else {
      const newLabels = [...labels];
      if (newLabels.length === 2) {
        newLabels.pop();
      }
      newLabels.push(label);
      setLabels(newLabels);
    }
  };

  const yAxisIds = useMemo(
    () => new Set(yAxisData.yAxis.map((axis) => axis.id)),
    [yAxisData.yAxis]
  );

  // memoized booleans
  const hasLandOnly = useMemo(
    () => labels.length === 1 && labels.length === yAxisData.yAxis.length,
    [labels, yAxisData.yAxis]
  );

  const hasNw = useMemo(
    () =>
      labels.length === yAxisData.yAxis.length &&
      labels.length > 1 &&
      labels[1] === 'Networth' &&
      yAxisIds.has('nwAxis'),
    [labels, yAxisData.yAxis, yAxisIds]
  );

  const hasHonor = useMemo(
    () =>
      labels.length === yAxisData.yAxis.length &&
      labels.length > 1 &&
      labels[1] === 'Honor' &&
      yAxisIds.has('honorAxis'),
    [labels, yAxisData.yAxis, yAxisIds]
  );

  return (
    <Box sx={{ marginTop: 2, marginBottom: 2 }}>
      <Grid container sx={{ justifyContent: 'center' }}>
        <Grid sx={{ px: 1, textAlign: 'center' }}>
          <Box sx={{ fontSize: '10px' }}>Networth</Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              size='small'
              checked={labels.includes('Networth')}
              onClick={() => handleLabelClick('Networth')}
            />
          </Box>
        </Grid>
        <Grid sx={{ px: 1, textAlign: 'center' }}>
          <Box sx={{ fontSize: '10px' }}>Honor</Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              size='small'
              checked={labels.includes('Honor')}
              onClick={() => handleLabelClick('Honor')}
            />
          </Box>
        </Grid>
      </Grid>
      {(hasLandOnly || hasNw || hasHonor) && (
        <LineChart
          height={450}
          margin={{ top: 80, bottom: 60, left: 0, right: 100 }}
          dataset={dataSet}
          series={yAxisData.series}
          xAxis={[
            {
              dataKey: 'tick',
              id: 'x-axis-id',
              scaleType: 'linear',
              min: dataSet.length
                ? Math.min(...dataSet.map((item) => Number(item.tick)))
                : 0,
              max: dataSet.length
                ? Math.max(...dataSet.map((item) => Number(item.tick)))
                : 100,
              valueFormatter: (v) => v.toLocaleString(),
            },
          ]}
          yAxis={yAxisData.yAxis}
          sx={{
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <LinePlot />
          <ChartsTooltip />
          <ChartsXAxis position='bottom' axisId='x-axis-id' />
          <ChartsYAxis position='left' axisId='landAxis' />
          {labels.includes('Networth') && (
            <ChartsYAxis
              position={
                labels.includes('Networth') && labels.includes('Honor')
                  ? 'left'
                  : 'right'
              }
              axisId='nwAxis'
            />
          )}
          {labels.includes('Honor') && (
            <ChartsYAxis label='Honor' position='right' axisId='honorAxis' />
          )}
          <ChartsLegend />
        </LineChart>
      )}
    </Box>
  );
};

export default NwLandGraph;
